class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
    this.enrichWithSkuResults();
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
        this.enrichWithSkuResults();
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  async enrichWithSkuResults() {
    const query = this.getQuery().trim();
    if (query.length < 2) return;

    // Only run SKU enrichment if query looks like a SKU (contains digit or hyphen)
    const looksLikeSku = /[\d\-]/.test(query);
    if (!looksLikeSku) return;

    // Cancel any previous SKU search in progress
    if (this._skuAbortController) {
      this._skuAbortController.abort();
    }
    this._skuAbortController = new AbortController();
    const signal = this._skuAbortController.signal;

    try {
      const targetId = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
      const targetElement = document.getElementById(targetId);
      if (!targetElement) return;

      // Hide empty state during SKU search to prevent flash
      const emptyState = targetElement.querySelector('.empty-state');
      if (emptyState) {
        emptyState.style.display = 'none';
      }

      // Fetch full search results page (Shopify search DOES find SKUs unlike predictive search)
      const searchUrl = new URL(`${window.shopUrl}/search`);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('q', query);

      const response = await fetch(searchUrl.toString(), { signal });
      if (!response.ok) return;

      const text = await response.text();
      const parser = new DOMParser();
      const searchDoc = parser.parseFromString(text, 'text/html');

      // Search results use product-card class
      const searchProductCards = searchDoc.querySelectorAll('.product-card');

      // Verify the query is still the same (user hasn't typed more)
      if (this.getQuery().trim() !== query) return;

      // If no SKU matches found, restore the empty state
      if (searchProductCards.length === 0) {
        if (emptyState) emptyState.style.display = '';
        return;
      }

      // Find or build the products list section
      let resultsContainer = targetElement.querySelector('#predictive-search-results-products-list');

      if (!resultsContainer) {
        // No products section exists (empty state shown), build it from scratch
        const emptyState = targetElement.querySelector('.empty-state');
        if (emptyState) emptyState.remove();

        const productsHtml = `
          <div class="flex w-full flex-col gap-y-6 md:flex-row">
            <div class="flex flex-col flex-grow search__box-products order-last md:order-first">
              <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
                <h4 class="h4 predictive-search-result__heading">${this.getProductsHeading()}</h4>
                <div class="swipe-mobile swipe-mobile--2-cols">
                  <div id="predictive-search-results-products-list" class="f-grid f-grid--gap-md f-grid--row-gap-md swipe-mobile__inner"></div>
                </div>
              </div>
            </div>
          </div>
        `;
        targetElement.insertAdjacentHTML('beforeend', productsHtml);
        resultsContainer = targetElement.querySelector('#predictive-search-results-products-list');
      }

      if (!resultsContainer) return;

      const existingLinks = new Set(
        Array.from(resultsContainer.querySelectorAll('a[href*="/products/"]')).map((a) => {
          // Normalize URL by removing query params
          try {
            const u = new URL(a.href);
            return u.pathname;
          } catch {
            return a.href;
          }
        })
      );

      let addedCount = 0;
      const maxResults = parseInt(this.dataset.resultsLimit || '3');

      Array.from(searchProductCards).forEach((card) => {
        if (addedCount >= maxResults) return;

        const productLink = card.querySelector('a[href*="/products/"]');
        if (!productLink) return;

        let pathname;
        try {
          const u = new URL(productLink.href);
          pathname = u.pathname;
        } catch {
          return;
        }

        if (!existingLinks.has(pathname)) {
          const clonedCard = card.cloneNode(true);
          resultsContainer.appendChild(clonedCard);
          existingLinks.add(pathname);
          addedCount++;
        }
      });

      // Make sure container is visible
      if (addedCount > 0) {
        this.searchContent?.classList.remove('hidden');
        this.setAttribute('results', '');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.debug('SKU enrichment error:', error.message);
      }
    }
  }

  getProductsHeading() {
    const existingHeading = document.querySelector('.predictive-search-result__heading');
    return existingHeading ? existingHeading.textContent.trim() : 'Productos';
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
